import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import {
  useDataQueryContext,
  useSearchParamsContext,
} from 'modules/linkExpirationManagement/contexts';
import { FC, PropsWithChildren, useCallback, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { paginate, sortObjectListByKey } from 'utils/arrayHelpers';
import { useModalContext } from 'utils/contexts/modal';
import { usePaginationContext } from 'utils/contexts/pagination';
import { linksArrayName } from '../constants';
import { LinksFieldArray, ModalState } from '../types';
import { createDirtyLinksPayload, createUpdateAllPayload } from '../utils';
import { FieldArrayContext } from './FieldArrayContext';

export const FieldArrayContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { handleUpdateLinks } = useDataQueryContext();
  const { formState, handleSubmit } = useFormContext<LinksFieldArray>();
  const { modalState } = useModalContext<ModalState>();
  const { currentPage, pageSize } = usePaginationContext();
  const { debouncedSearch, sortDirection } = useSearchParamsContext();

  const { fields = [] } = useFieldArray<LinksFieldArray>({
    name: linksArrayName,
  });

  /**
   * Creates a reference to the initial field values created by useFieldArray
   * which uses an index value to keep track of each field setting
   *
   * This reference attaches the original index value from `useFieldArray` so that
   * each field setting can be updated correctly when filtering/sorting the table
   */
  const fieldsReference = useMemo(
    () =>
      fields.map((field, index) => ({
        ...field,
        index,
      })),
    [fields],
  );

  /** Filtered fields based on name and id */
  const filteredFields = fieldsReference.filter(
    ({ projectId, projectName }) =>
      projectName.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      projectId.toString().includes(debouncedSearch),
  );

  const sortedAndPaginatedFields = useMemo(() => {
    const sortedFields = sortObjectListByKey(
      filteredFields,
      'projectName',
      sortDirection === SORT_DIRECTIONS.DESCENDING,
    );
    return paginate(sortedFields, pageSize, currentPage);
  }, [currentPage, filteredFields, pageSize, sortDirection]);

  const updateAllFields = useCallback(
    (values: LinksFieldArray) => {
      const setting = modalState?.payload?.setting;
      if (!!setting) {
        const payload = createUpdateAllPayload(values.links, setting);
        handleUpdateLinks(payload);
      }
    },
    [handleUpdateLinks, modalState?.payload?.setting],
  );

  const updateChangedFields = useCallback(
    (values: LinksFieldArray) => {
      const payload = createDirtyLinksPayload(values.links, formState?.dirtyFields);
      handleUpdateLinks(payload);
    },
    [formState?.dirtyFields, handleUpdateLinks],
  );

  return (
    <FieldArrayContext.Provider
      value={{
        fields: sortedAndPaginatedFields,
        onSubmitAllFields: handleSubmit(updateAllFields),
        onSubmitChangedFields: handleSubmit(updateChangedFields),
        totalFieldsCount: filteredFields.length,
      }}
    >
      {children}
    </FieldArrayContext.Provider>
  );
};
